import { Fragment, useState } from 'react';
import type { BossMatrixCharacter, IncomeResponse } from '../../lib/bosses';
import { groupBossesByTier, type BossTier } from '../../lib/bossGrouping';
import type { BossDto, ClassDto } from '../../lib/reference';
import { classDisplayName } from '../../lib/reference';
import { formatCrystalValue } from '../../lib/bossGrouping';
import { BossCell } from './BossCell';
import { BossIcon } from './BossIcon';
import { CapMeter } from './CapMeter';

interface BossMatrixGridProps {
  characters: BossMatrixCharacter[];
  /** REV18: for friendly class names in the header microtext — falls back to the raw slug when
   *  the catalog hasn't loaded yet or a slug is unrecognized. */
  classes: ClassDto[];
  bossCatalog: BossDto[];
  income: IncomeResponse | undefined;
  editableForTier: (tier: BossTier) => boolean;
  periodKeyForTier: (tier: BossTier) => string;
  planPendingKey: string | null;
  clearPendingKey: string | null;
  /** `5` on a Heroic account, `1` otherwise — see `BossCellProps.crystalMultiplier`. */
  crystalMultiplier: number;
  onSetDifficulty: (character: BossMatrixCharacter, boss: BossDto, difficulty: string) => void;
  onSetCleared: (character: BossMatrixCharacter, boss: BossDto, periodKey: string, cleared: boolean) => void;
  onSetCrystalSold: (character: BossMatrixCharacter, boss: BossDto, periodKey: string, crystalSold: boolean) => void;
}

/**
 * Desktop/tablet layout (≥md): Rebrand-Maple orientation — columns = characters, rows = bosses
 * grouped by tier (WEEKLY on top, then MONTHLY, then a collapsed-by-default DAILY section).
 * Horizontally scrolls inside its own container with a sticky first column, mirroring `WeekGrid`.
 */
export function BossMatrixGrid({
  characters,
  classes,
  bossCatalog,
  income,
  editableForTier,
  periodKeyForTier,
  planPendingKey,
  clearPendingKey,
  crystalMultiplier,
  onSetDifficulty,
  onSetCleared,
  onSetCrystalSold,
}: BossMatrixGridProps) {
  const [dailyExpanded, setDailyExpanded] = useState(false);
  const groups = groupBossesByTier(bossCatalog);

  if (characters.length === 0) {
    return (
      <p className="text-sm text-text-muted">Add a character under Characters to start tracking bosses.</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-bg-elevated">
      {/* min-w bumped for the full-name difficulty labels (2026-08-16): the select alone can be up
          to ~10.5rem now, plus the inline clear/sold controls next to it — this table already lives
          inside its own overflow-x-auto container, so wider columns just extend the scroll range
          rather than forcing the page body to scroll. */}
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-bg-elevated-hover">
            <th className="sticky left-0 z-10 min-w-[9rem] bg-bg-elevated-hover px-3 py-2 text-left font-display text-xs font-semibold uppercase tracking-wider text-text-faint">
              Boss
            </th>
            {characters.map((character) => (
              <th key={character.characterId} className="min-w-[14rem] px-2 py-2 text-center">
                <p className="truncate text-sm font-semibold text-text">{character.name}</p>
                <p className="truncate text-xs text-text-faint">
                  {classDisplayName(character.classSlug, classes)} · Lv.{character.level}
                </p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const isDaily = group.tier === 'DAILY';
            const collapsed = isDaily && !dailyExpanded;
            return (
              <Fragment key={group.tier}>
                <tr className="bg-bg-elevated-hover">
                  <td
                    colSpan={characters.length + 1}
                    className="sticky left-0 bg-bg-elevated-hover px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-widest text-text-faint"
                  >
                    {isDaily ? (
                      <button
                        type="button"
                        onClick={() => setDailyExpanded((v) => !v)}
                        className="flex min-h-tap items-center gap-1.5 text-text-faint hover:text-text-muted"
                        aria-expanded={dailyExpanded}
                      >
                        <span aria-hidden="true">{dailyExpanded ? '▾' : '▸'}</span>
                        {group.label} bosses ({group.bosses.length})
                      </button>
                    ) : (
                      `${group.label} bosses`
                    )}
                  </td>
                </tr>
                {!collapsed &&
                  group.bosses
                    // A boss hidden by every character in the account has nothing left to show for
                    // it — the whole row disappears rather than rendering an all-dashes row.
                    .filter((boss) => !characters.every((c) => c.hiddenBossSlugs.includes(boss.slug)))
                    .map((boss) => {
                      const periodKey = periodKeyForTier(boss.tier);
                      const editable = editableForTier(boss.tier);
                      return (
                        <tr key={boss.slug} className="border-b border-border last:border-0 hover:bg-bg-elevated-hover">
                          <td className="sticky left-0 z-10 min-w-[9rem] bg-bg-elevated px-3 py-1.5 align-middle">
                            <div className="flex items-center gap-2">
                              <BossIcon slug={boss.slug} name={boss.name} />
                              <p className="truncate text-sm font-medium text-text">{boss.name}</p>
                            </div>
                          </td>
                          {characters.map((character) => {
                            if (character.hiddenBossSlugs.includes(boss.slug)) {
                              return (
                                <td
                                  key={character.characterId}
                                  className="px-2 py-1 text-center text-text-faint"
                                  aria-label={`${boss.name} hidden for ${character.name}`}
                                >
                                  —
                                </td>
                              );
                            }
                            const plan = character.plans.find((p) => p.bossSlug === boss.slug);
                            const clear = character.clears.find(
                              (c) => c.bossSlug === boss.slug && c.periodKey === periodKey,
                            );
                            return (
                              <td key={character.characterId} className="px-2 py-1 text-center">
                                <BossCell
                                  boss={boss}
                                  character={character}
                                  characterName={character.name}
                                  plan={plan}
                                  clear={clear}
                                  editable={editable}
                                  planPending={planPendingKey === `${character.characterId}:${boss.slug}`}
                                  clearPending={clearPendingKey === `${character.characterId}:${boss.slug}`}
                                  crystalMultiplier={crystalMultiplier}
                                  onSetDifficulty={(difficulty) => onSetDifficulty(character, boss, difficulty)}
                                  onSetCleared={(cleared) => onSetCleared(character, boss, periodKey, cleared)}
                                  onSetCrystalSold={(sold) => onSetCrystalSold(character, boss, periodKey, sold)}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
              </Fragment>
            );
          })}
          <tr className="border-t border-border-strong bg-bg-elevated-hover">
            <td className="sticky left-0 bg-bg-elevated-hover px-3 py-2 font-display text-xs font-semibold uppercase tracking-widest text-text-faint">
              Weekly total
            </td>
            {characters.map((character) => {
              const stats = income?.characters.find((c) => c.characterId === character.characterId);
              return (
                <td key={character.characterId} className="px-2 py-2 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <CapMeter
                      label="Crystals"
                      current={stats?.weeklyCrystalsSold ?? 0}
                      cap={stats?.characterCap ?? 14}
                      reached={stats?.characterCapReached ?? false}
                      size="sm"
                    />
                    <span className="text-xs font-semibold tabular-nums text-crystal">
                      {formatCrystalValue(stats?.totalMesos ?? 0)}
                    </span>
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
